import { applyTheme, currentTheme, rememberTheme } from './theme-memory';
import type { Theme } from '../core/theme/theme';

/**
 * Who is watching the appearance, so every switch on the page agrees with every other.
 *
 * **The class on `<html>` is still the authority and this store never caches it.** The snapshot
 * reads the class each time, because three other things write it and none of them goes through
 * here: the inline script in `index.html` before first paint, `main.tsx` on start-up, and the
 * cascade itself once the class is there. A cached copy would be a fourth answer, and the one the
 * button describes.
 *
 * The listeners exist only so a `useSyncExternalStore` reader is told to look again. Nothing is
 * passed to them.
 */
const listeners = new Set<() => void>();

export function subscribeToTheme(listener: () => void): () => void {
    listeners.add(listener);

    return () => {
        listeners.delete(listener);
    };
}

/** What is on screen, read from the class the page is painted by. */
export function getTheme(): Theme {
    return currentTheme();
}

/**
 * Light, where there is no document to read a class from.
 *
 * A build-time render has no visitor and therefore no remembered choice, so every prerendered page
 * carries the light switch. The class itself is applied before first paint by the inline script, so
 * a returning visitor still *sees* the dark page — it is only the icon in the switch that settles a
 * moment later, once React has read the class it was already painted with.
 */
export function getServerTheme(): Theme {
    return 'light';
}

/** Remembers the choice, paints it, and tells everyone showing a switch. */
export function setTheme(theme: Theme): void {
    rememberTheme(theme);
    applyTheme(theme);

    for (const listener of listeners) {
        listener();
    }
}
